import { Add, ArrowBack, FileCopy, PublishedWithChanges } from "@mui/icons-material";
import { Box, Button, Chip, CircularProgress, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import { useAtom } from "jotai";
import { FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { modalNewAdmission, permissionsAtom } from "../../atoms/atom";
import AppBar from "../../components/app-bar/AppBar";
import NewAdmissionModal from "../../components/audit/new-admission-modal/NewAdmissionModal";
import { AuditService } from "../../services/AuditService";
import { AdmissionStatusDTO } from "../../types/AdmissionStatusDTO";
import { AuditAdmissionDTO } from "../../types/AuditAdmissionDTO";
import { useUtils } from "../../utils/utilFunctions";
import { getLocation, stepStatuses, StepStatusKey } from "../audit-admission/AuditAdmission";
import { getLoading } from "../execution-events/ExecutionEvents";
import { obtemIconeOrdem } from "../executions/Executions";
import "./AuditAdmissionList.scss";

type Header = 'NAME' | 'DATE' | 'STATUS' | 'EXPIRES';

const AuditAdmissionList: FC = () => {
	const { id } = useParams<{ id: string }>();
	const [open, setOpen] = useAtom(modalNewAdmission);
	const [permissions] = useAtom(permissionsAtom);
	const [search, setSearch] = useState('');
	const [loading, setLoading] = useState(true);
	const [admissions, setAdmissions] = useState<AuditAdmissionDTO[]>([]);
	const [ordemAsc, setOrdemAsc] = useState(false);
	const [fieldOrder, setFieldOrder] = useState<Header>('DATE');
	const service = new AuditService();
	const navigate = useNavigate();
	const { formatStringFullDate, handleCopy, verifyPermission } = useUtils();

	function mudaOrdem(field: Header) {
		setFieldOrder(field);
		setOrdemAsc(!ordemAsc);
	}

	const handleClickLink = () => {
		setOpen(true);
	}

	const loadAdmissions = async () => {
		const response = await service.getAdmissions(id || '');
		if (response) {
			setAdmissions(response.data);
		}
		setLoading(false);
	};

	useEffect(() => {
		if (!id) return;
		if (!!permissions.length) {
			verifyPermission(permissions, 'AUDIT');
		}
		if (open) return;
		loadAdmissions();
	}, [open]);

	return (
		<>
			<AppBar
				modulo="Admissões"
			/>
			<div className="audit-admissions">
				<div className="top">
					<Button
						variant="contained"
						color="info"
						onClick={() => navigate('/auditoria')}
						startIcon={<ArrowBack />}
					>
						Voltar para empresas
					</Button>
					<Button
						onClick={handleClickLink}
						variant='contained'
						color='success'
						startIcon={<Add />}
					>
						Gerar link de admissão
					</Button>
					<TextField
						label="Buscar candidato por nome"
						id="group-search"
						size="small"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
					/>
				</div>
				{loading
					? getLoading()
					: filter()?.length === 0
						? <div className="sem-execucoes">Nenhum registro encontrado para o filtro aplicado...</div>
						: <div className="tabela">
							<div className="header-tabela">
								<p className="pointer" onClick={() => mudaOrdem('NAME')}>Nome {obtemIconeOrdem('NAME', fieldOrder, ordemAsc)}</p>
								<p className="pointer" onClick={() => mudaOrdem('DATE')}>Link criado em {obtemIconeOrdem('DATE', fieldOrder, ordemAsc)}</p>
								<p className="pointer" onClick={() => mudaOrdem('EXPIRES')}>Data de expiração {obtemIconeOrdem('EXPIRES', fieldOrder, ordemAsc)}</p>
								<p>Documentos</p>
								<p className="pointer" onClick={() => mudaOrdem('STATUS')}>Status {obtemIconeOrdem('STATUS', fieldOrder, ordemAsc)}</p>
								<p>Link</p>
								<p>Ações</p>
							</div>
							<div className="body-table">
								{filter().map((admission) => (
									<div
										key={admission.id}
										id={admission.id + ''}
										className="linha-empresa pointer"
										onClick={() => navigate(`/auditoria/${id}/admissoes/${admission.id}`)}
									>
										<p>{admission.name}</p>
										<p>{formatStringFullDate(admission.createdAt)}</p>
										<p>{admission.expiresAt ? formatStringFullDate(admission.expiresAt) : "Nunca"}</p>
										<p>
											<Tooltip title={`${admission.documents?.length || 0} de 12 documentos enviados`}>
												<Box sx={{ position: 'relative', display: 'inline-flex' }}>
													<CircularProgress
														variant="determinate"
														value={((admission.documents?.length || 0) / 12) * 100}
														color={(admission.documents?.length || 0) < 12 ? "warning" : "success"}
														sx={{height: "6vh !important", width: "6vh !important"}}
													/>
													<Box
														sx={{
															top: 0,
															left: 0,
															bottom: 0,
															right: 0,
															position: 'absolute',
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center',
														}}
													>
														<Typography variant="caption" component="div" color="text.secondary">
															{`${admission.documents?.length || 0}/12`}
														</Typography>
													</Box>
												</Box>
											</Tooltip>
										</p>
										<p>
											<Chip
												icon={stepStatuses[admission.stepStatus as StepStatusKey].icon}
												label={stepStatuses[admission.stepStatus as StepStatusKey].status}
												variant="outlined"
												sx={{ borderRadius: 4 }}
											/>
										</p>
										<p>
											<Tooltip title={getLabelLink(admission)}>
												{admission.step !== 4 && (!admission.expiresAt || admission.expiresAt > new Date().getTime())
													? <IconButton onClick={(e) => {
														e.stopPropagation();
														handleCopy(`https://interno.inovação.seg.br/auditoria/nova-admissao/${admission.id}`);
													}}>
														<FileCopy/>
													</IconButton>
													: <Chip
														color="error"
														label="Fechado"
														variant="outlined"
														sx={{ borderRadius: 4 }}
													/>
												}
											</Tooltip>
										</p>
										<p>
											{admission.step === 4 && admission.expiresAt > new Date().getTime()
												? <Tooltip title="Reabrir link">
													<IconButton
														onClick={(e) => {
															e.stopPropagation();
															reopen(admission.id);
														}}
													>
														<PublishedWithChanges color="success"/>
													</IconButton>
												</Tooltip>
												: <></>
											}
										</p>
									</div>
								))}
							</div>
						</div>
				}
			</div>
			<NewAdmissionModal
				id={Number(id)}
			/>
		</>
	);

	function filter() {
		let filtered = [...admissions];
		if (search) {
			filtered = filtered.filter(item => item.name.toLowerCase().includes(search.toLowerCase()));
		}
		return filtered.sort((a, b) => {
			let valueA: string | number;
			let valueB: string | number;
			switch (fieldOrder) {
				case "NAME":
					valueA = a.name;
					valueB = b.name;
					break;
				case 'DATE':
					valueA = formatStringFullDate(a.createdAt);
					valueB = formatStringFullDate(b.createdAt);
					break;
				case 'STATUS':
					valueA = a.stepStatus;
					valueB = b.stepStatus;
					break;
				case 'EXPIRES':
					valueA = new Date(a.expiresAt).getTime();
					valueB = new Date(b.expiresAt).getTime();
					break;
			}
			if (typeof valueA === 'string' && typeof valueB === 'string') {
				return ordemAsc
					? valueA.localeCompare(valueB)
					: valueB.localeCompare(valueA);
			} else {
				return ordemAsc
					? (valueA as number) - (valueB as number)
					: (valueB as number) - (valueA as number);
			}
		});
	}

	async function reopen(id: number) {
		try {
			const { latitude, longitude, locationName } = await getLocation();
			const logPayload = {
				latitude,
				longitude,
				locationName,
			} as AdmissionStatusDTO;
			await service.reopenLink(id, logPayload);
			loadAdmissions();
		} catch (error) {
			console.log(error);
		}
	}

	function getLabelLink(v: AuditAdmissionDTO) {
		return v.step === 4
			? "Formulário preenchido pelo candidato"
			: v.expiresAt === null
				? "Copiar link de admissão"
				: v.expiresAt < new Date().getTime()
					? "Link fechado pela data de expiração"
					: "Copiar link de admissão";
	}
};

export default AuditAdmissionList;